'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { AddToBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketFragmentFragmentDoc';
import type {
  AddToBasketFragmentFragment,
  LineItemFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import type { ButtonProps } from '@packages/shared/src/ui/Button/Button';
import { Button, ButtonLink } from '@packages/shared/src/ui/Button/Button';
import type { DispatchButtonTrackingProps } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import { dispatchButtonTracking } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import React, { useEffect } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';
import { addToBasketMessages } from './messages';
import { useBasketData } from './useBasketData';

export type AddToBasketButtonTailwindProps = {
  data: FragmentType<typeof AddToBasketFragmentFragmentDoc>;
  children?: (
    layerData: AddToBasketFragmentFragment,
    lineItem: LineItemFragmentFragment,
  ) => React.ReactNode;
  lineItemId?: string;
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  customButtonText?: MessageDescriptor | null;
  hideEndIcon?: boolean;
  className?: string;
  iconClassName?: string;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  clickTrackingProps?: DispatchButtonTrackingProps['clickTrackingProps'];
};

export const AddToBasketButtonTailwind = ({
  data: productData,
  children: dialogChildren,
  lineItemId,
  onClose,
  onClick,
  customButtonText,
  hideEndIcon = false,
  className,
  iconClassName = '',
  color = 'primary',
  variant = 'textLink',
  size = 'base',
  clickTrackingProps,
}: AddToBasketButtonTailwindProps) => {
  const config = useConfig();
  const { locale } = useI18n();
  const dispatchEvent = useTracking();
  const { formatMessage } = useIntl();
  const { loggedIn } = useLoginState();
  const {
    product,
    isAvailable,
    isLoading,
    dialogOpened,
    miniDVOnClickHandler,
    onClickHandler,
    trackToCheckAndOrderHandler,
    layerCloseHandler,
    handleButtonText,
    lineItemForDialog,
  } = useBasketData({
    productData,
    lineItemId,
    onClose,
    customButtonText,
  });

  const endIconElement = (() => {
    if (hideEndIcon) {
      return null;
    }
    if (isLoading) {
      return <CircularProgress className={`text-color-secondary ${iconClassName}`} />;
    }
    if (!isLoading && lineItemId === undefined) {
      return (
        <div
          className={cn('icon-emp-cart text-color-primary-contrasttext size-4', iconClassName)}
        />
      );
    }
    return null;
  })();

  const handleExternalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatchButtonTracking({
      eventDispatcher: dispatchEvent,
      clickTrackingProps,
      type: 'button',
    });
    onClick?.(event);
    if (lineItemId) {
      miniDVOnClickHandler(event);
    } else {
      onClickHandler(event);
    }
  };
  useEffect(() => {
    dialogOpened &&
      dispatchDisplayOverlayTracking({
        eventDispatcher: dispatchEvent,
        displayTrackingProps: {
          category: 'A2B-Layer',
          label: 'A2B',
        },
      });
  }, [dialogOpened, dispatchEvent]);
  return (
    <>
      {/* If there is no onClose function, it means button was clicked from detail view page */}
      {dialogOpened && lineItemForDialog && onClose === undefined && (
        <Dialog className="w-full" isOpen={dialogOpened} onClose={layerCloseHandler}>
          <DialogHeader onClose={layerCloseHandler}>
            {formatMessage(addToBasketMessages.headline)}
          </DialogHeader>
          <DialogContent className="p-0">
            {dialogChildren &&
              product &&
              lineItemForDialog &&
              dialogChildren(product, lineItemForDialog)}
          </DialogContent>
          <DialogFooter>
            {loggedIn && (
              <ButtonLink
                className="w-full md:max-w-[180px]"
                href={getLocalizedPath(config.order.paths.payment, locale)}
                onClick={trackToCheckAndOrderHandler}
                color="primary"
              >
                {formatMessage(addToBasketMessages.toCheckout)}
              </ButtonLink>
            )}
            <ButtonLink
              className="w-full md:max-w-[180px]"
              onClick={trackToCheckAndOrderHandler}
              href={getLocalizedPath(config.order.paths.basket, locale)}
              color={loggedIn ? 'default' : 'primary'}
            >
              {formatMessage(addToBasketMessages.navigateToCart)}
            </ButtonLink>
            <Button
              className="w-full md:max-w-[180px]"
              onClick={layerCloseHandler}
              variant="icon-flat"
            >
              {formatMessage(addToBasketMessages.continueShopping)}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
      <Button
        className={cn('w-full whitespace-nowrap', className)}
        onClick={handleExternalClick}
        size={size}
        variant={variant}
        color={color}
        disabled={!isAvailable || isLoading}
      >
        {customButtonText !== null && formatMessage(handleButtonText())}
        {endIconElement && endIconElement}
      </Button>
    </>
  );
};
